// Órdenes de Trabajo (OT) — the despacho's operational file on one fianza.
//
// An OT is the vehicle the team moves through its own fases (configured per
// org, each fase optionally owned by an equipo). It is NOT a pendiente and it
// does NOT replace Pulso: Pulso is the cartera view of every requisito, the OT
// is the work surface of the team that operates the trámite, for the orgs that
// turn the feature on (orgs/{org}.otEnabled).
//
// Decisions (Fran, 2026-10-17):
//   - One OT per fianza. A contrato with three fianzas requeridas is three OTs.
//   - Created by hand, never automatically.
//   - Can be born with no relation and get the contrato / fianza later.
//   - Extra work that comes up while the OT is open (an endoso on the fianza
//     in process) is a pendiente or a note in the hilo, not a structure of its
//     own; the `tareas[]` list was removed 2026-10-19 ("duplica Pendientes").
//     An endoso on a fianza already delivered is a NEW OT (tipo 'endoso').
//   - Relevo between fases (2026-10-19): whoever works the fase declares it
//     terminada; a coordinador (config.coordinadores) passes the OT to the
//     next fase and picks who works it. Asignados are per fase.
//   - Closing is by hand, at any time, documented (who, when, resultado,
//     motivo). Nothing is deleted; a closed OT keeps its historial. Reopening
//     is recorded the same way.
//   - Pendientes filed from an OT carry `otId` (+ `equipoId` of the equipo
//     being asked). They show in the OT and in the normal Pendientes surfaces
//     and never move the OT.
//   - Folio is a plain running number per org (OT 1, OT 2, OT 1234). No
//     padding: this despacho expects ~6k OTs a year.
//
// Pure functions only — no React, no Firestore imports. Callers persist the
// returned objects. The equipos/fases config lives in orgs/{org}/config/ot.

export const OT_TIPOS = {
  FIANZA: 'fianza',
  ENDOSO: 'endoso',
};

export const OT_TIPO_LABELS = {
  fianza: 'Fianza',
  endoso: 'Endoso',
};

export const OT_ESTATUS = {
  ABIERTA: 'abierta',
  CERRADA: 'cerrada',
};

export const OT_ESTATUS_LABELS = {
  abierta: 'Abierta',
  cerrada: 'Cerrada',
};

// How an OT ended. "entregada" is the normal end — the fianza is in the fiado's
// hands. The others exist because closing has to be possible at any time; the
// motivo says why.
export const OT_RESULTADOS = {
  ENTREGADA: 'entregada',
  CANCELADA: 'cancelada',
  OTRO: 'otro',
};

export const OT_RESULTADO_LABELS = {
  entregada: 'Entregada',
  cancelada: 'Cancelada',
  otro: 'Otro',
};

export const TITULO_MAX_LEN = 140;
export const DESCRIPCION_MAX_LEN = 1000;
export const MENSAJE_MAX_LEN = 1000;
export const MOTIVO_MAX_LEN = 300;

// ---------------------------------------------------------------------------
// Config: equipos and fases
// ---------------------------------------------------------------------------

// Seeded for a new org (Fran's example: "one for the lectura documental, one
// for the suscripción and another for comisiones"). The owner edits in
// Configuración → Órdenes de trabajo; what is stored wins.
export const EQUIPOS_DEFAULT = [
  { id: 'lectura', nombre: 'Lectura documental', miembros: [] },
  { id: 'suscripcion', nombre: 'Suscripción', miembros: [] },
  { id: 'comisiones', nombre: 'Comisiones', miembros: [] },
];

// EACH TIPO HAS ITS OWN FLUJO (Fran, 2026-10-17: "le pones seleccionar fianza
// o endoso pero luego me dejas hacer lo que sea; las fases las quiero poder yo
// determinar en la configuración, y los tiempos también"). A fase carries the
// equipo that owns it and the días objetivo the org expects it to take; the
// tarjeta and the stats compare against that.
export const FASES_FIANZA_DEFAULT = [
  { id: 'solicitud', nombre: 'Solicitud', equipoId: null, diasObjetivo: 1 },
  { id: 'documentacion', nombre: 'Documentación', equipoId: 'lectura', diasObjetivo: 3 },
  { id: 'suscripcion', nombre: 'Suscripción', equipoId: 'suscripcion', diasObjetivo: 2 },
  { id: 'emision', nombre: 'Emisión', equipoId: 'suscripcion', diasObjetivo: 1 },
  { id: 'entrega', nombre: 'Entrega', equipoId: null, diasObjetivo: 1 },
  { id: 'comisiones', nombre: 'Comisiones', equipoId: 'comisiones', diasObjetivo: 30 },
];

export const FASES_ENDOSO_DEFAULT = [
  { id: 'solicitud', nombre: 'Solicitud', equipoId: null, diasObjetivo: 1 },
  { id: 'suscripcion', nombre: 'Suscripción', equipoId: 'suscripcion', diasObjetivo: 2 },
  { id: 'emision', nombre: 'Emisión', equipoId: 'suscripcion', diasObjetivo: 1 },
  { id: 'entrega', nombre: 'Entrega', equipoId: null, diasObjetivo: 1 },
];

// Kept for callers that only know the fianza flow.
export const FASES_DEFAULT = FASES_FIANZA_DEFAULT;

// THE ORG DEFINES ITS OWN TIPOS (Fran, 2026-10-17: "necesitas también dejarme
// crear distintos tipos de OT aparte de la de Fianza y la de Endosos"). A
// tipo IS a flujo: id, nombre, fases. `fianza` and `endoso` are the two base
// tipos — always present, renamable, never deletable — because the dialog
// treats them specially (a fianza has no fianza yet, an endoso requires one).
// Everything else is a plain flow with an optional relation.
export const TIPOS_BASE = [OT_TIPOS.FIANZA, OT_TIPOS.ENDOSO];

export const FLUJOS_DEFAULT = [
  { id: OT_TIPOS.FIANZA, nombre: 'Fianza', fases: FASES_FIANZA_DEFAULT },
  { id: OT_TIPOS.ENDOSO, nombre: 'Endoso', fases: FASES_ENDOSO_DEFAULT },
];

export const CONFIG_OT_DEFAULT = {
  equipos: EQUIPOS_DEFAULT,
  flujos: FLUJOS_DEFAULT,
  coordinadores: [],
  aprobadores: [],
};

const limpiarLista = (v) => (Array.isArray(v) ? v : []);

const limpiarFase = (f) => ({
  id: f.id,
  nombre: f.nombre || '',
  equipoId: f.equipoId || null,
  diasObjetivo: Number.isFinite(Number(f.diasObjetivo)) && Number(f.diasObjetivo) > 0
    ? Number(f.diasObjetivo) : null,
  // An approval fase (Fran, 2026-10-19, "anulaciones"): nobody terminates
  // it; an aprobador approves (relevo) or rejects (back, motivo required).
  requiereAprobacion: Boolean(f.requiereAprobacion),
});

const flujoDefault = (id) => {
  const d = FLUJOS_DEFAULT.find((f) => f.id === id);
  return d ? { id: d.id, nombre: d.nombre, fases: d.fases.map((f) => ({ ...f })) } : null;
};

/**
 * The effective config: stored lists when present, defaults otherwise. A
 * stored but EMPTY fases list is still honoured as empty — the owner may have
 * removed everything on purpose — but a missing flujo means "never
 * configured". Earlier shapes are read too: `flujos` as an object keyed by
 * tipo (second build), and a root `fases` list (first build) = the fianza
 * flow. The base tipos are always present, in front.
 *
 * @param {Object|null|undefined} stored  - orgs/{org}/config/ot data
 * @return {{equipos:Array<Object>, flujos:Array<{id:string,nombre:string,fases:Array}>, coordinadores:Array<string>}}
 */
export function resolverConfigOt(stored) {
  const s = stored || {};
  let lista = [];
  if (Array.isArray(s.flujos)) {
    lista = s.flujos;
  } else if (s.flujos && typeof s.flujos === 'object') {
    lista = Object.entries(s.flujos).map(([id, v]) => ({ id, nombre: v?.nombre, fases: v?.fases }));
  } else if (Array.isArray(s.fases)) {
    lista = [{ id: OT_TIPOS.FIANZA, fases: s.fases }];
  }
  const porId = {};
  lista.forEach((f) => {
    if (!f?.id) return;
    const base = flujoDefault(f.id);
    porId[f.id] = {
      id: f.id,
      nombre: f.nombre || base?.nombre || f.id,
      fases: Array.isArray(f.fases) ? f.fases.map(limpiarFase) : (base?.fases || []),
    };
  });
  const flujos = [
    ...TIPOS_BASE.map((id) => porId[id] || flujoDefault(id)),
    ...lista.filter((f) => f?.id && !TIPOS_BASE.includes(f.id)).map((f) => porId[f.id]),
  ];
  return {
    equipos: Array.isArray(s.equipos)
      ? s.equipos.map((e) => ({ id: e.id, nombre: e.nombre || '', miembros: limpiarLista(e.miembros) }))
      : EQUIPOS_DEFAULT.map((e) => ({ ...e, miembros: [] })),
    flujos,
    // Who runs the relevo between fases (Fran, 2026-10-19): not a usuarios
    // role, a list the owner keeps in Configuración.
    coordinadores: limpiarLista(s.coordinadores).filter((u) => typeof u === 'string' && u),
    // Who signs approval fases; empty = the coordinadores.
    aprobadores: limpiarLista(s.aprobadores).filter((u) => typeof u === 'string' && u),
  };
}

/**
 * Whether a uid approves: named in `aprobadores`, or a coordinador while
 * nobody has been named.
 * @param {Object} config  - resolved
 * @param {string} uid
 * @param {boolean} [esOwner]
 * @return {boolean}
 */
export function esAprobador(config, uid, esOwner = false) {
  if (!uid) return false;
  const lista = limpiarLista(config?.aprobadores);
  if (lista.length) return lista.includes(uid);
  return esCoordinador(config, uid, esOwner);
}

/** Whether the OT sits in a fase that needs an approval. @param {Object} ot @param {Object} config */
export function esperaAprobacion(ot, config) {
  if (!isAbierta(ot)) return false;
  const fase = faseDe(fasesDe(config, ot?.tipo), ot?.faseId);
  return Boolean(fase?.requiereAprobacion);
}

/**
 * Whether a uid coordinates the org's órdenes: named in `coordinadores`, or
 * the owner while nobody has been named yet (so the bandeja is never
 * unreachable on a fresh org).
 * @param {Object} config  - resolved
 * @param {string} uid
 * @param {boolean} [esOwner]
 * @return {boolean}
 */
export function esCoordinador(config, uid, esOwner = false) {
  if (!uid) return false;
  const lista = limpiarLista(config?.coordinadores);
  if (lista.includes(uid)) return true;
  return esOwner && lista.length === 0;
}

/** @param {string} tipo @return {boolean} */
export function esTipoBase(tipo) {
  return TIPOS_BASE.includes(tipo);
}

/**
 * The flow (tipo) by id.
 * @param {Object} config  - resolved config
 * @param {string} tipo
 * @return {{id:string,nombre:string,fases:Array}|null}
 */
export function flujoDe(config, tipo) {
  return (config?.flujos || []).find((f) => f.id === tipo) || null;
}

/**
 * The fases of one tipo's flow.
 * @param {Object} config  - resolved config
 * @param {string} tipo
 * @return {Array<Object>}
 */
export function fasesDe(config, tipo) {
  return flujoDe(config, tipo)?.fases || [];
}

/**
 * Display name of a tipo: the org's name for the flow, else the base label,
 * else the id (an OT whose flow was deleted still reads).
 * @param {Object} config
 * @param {string} tipo
 * @return {string}
 */
export function nombreTipo(config, tipo) {
  return flujoDe(config, tipo)?.nombre || OT_TIPO_LABELS[tipo] || tipo || '';
}

/** Every fase of every flow, for rollups that span tipos. */
export function todasLasFases(config) {
  return (config?.flujos || []).flatMap((f) => f.fases || []);
}

/**
 * Sum of the días objetivo of a flow — what the org expects an OT to take
 * end to end. Null when no fase has one.
 * @param {Array<Object>} fases
 * @return {number|null}
 */
export function diasObjetivoTotal(fases) {
  const con = (fases || []).filter((f) => f.diasObjetivo);
  return con.length ? con.reduce((a, f) => a + f.diasObjetivo, 0) : null;
}

/**
 * A stable id for a new equipo or fase, derived from its name so the kanban
 * columns and the stats stay readable in Firestore, with a suffix when taken.
 *
 * @param {string} nombre
 * @param {Array<string>} tomados
 * @return {string}
 */
export function idDesdeNombre(nombre, tomados = []) {
  const base = String(nombre || '')
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32) || 'item';
  if (!tomados.includes(base)) return base;
  let n = 2;
  while (tomados.includes(`${base}-${n}`)) n += 1;
  return `${base}-${n}`;
}

/**
 * Form-level validation for the config screen. Returns the message to show
 * or null.
 *
 * @param {{equipos:Array<Object>, fases:Array<Object>}} config
 * @return {string|null}
 */
export function validarConfigOt(config) {
  // The resolver fills a blank nombre with the id so stored docs always read;
  // the screen must not get that courtesy.
  const crudos = Array.isArray(config?.flujos) ? config.flujos : [];
  if (crudos.some((f) => f && !String(f.nombre || '').trim())) return 'Cada tipo de orden necesita nombre.';
  const { equipos, flujos } = resolverConfigOt(config);
  if (equipos.some((e) => !String(e.nombre || '').trim())) return 'Cada equipo necesita nombre.';
  const eids = equipos.map((e) => e.id);
  if (new Set(eids).size !== eids.length) return 'Hay equipos repetidos.';
  const fids = flujos.map((f) => f.id);
  if (new Set(fids).size !== fids.length) return 'Hay tipos de orden repetidos.';
  for (const flujo of flujos) {
    const nombre = flujo.nombre || flujo.id;
    if (!String(flujo.nombre || '').trim()) return 'Cada tipo de orden necesita nombre.';
    const fases = flujo.fases;
    if (fases.length === 0) return `Hace falta al menos una fase en ${nombre}.`;
    if (fases.some((f) => !String(f.nombre || '').trim())) return `Cada fase necesita nombre (${nombre}).`;
    const ids = fases.map((f) => f.id);
    if (new Set(ids).size !== ids.length) return `Hay fases repetidas en ${nombre}.`;
    const huerfana = fases.find((f) => f.equipoId && !eids.includes(f.equipoId));
    if (huerfana) return `La fase ${huerfana.nombre} apunta a un equipo que ya no existe.`;
  }
  return null;
}

/** @param {Array<Object>} fases @param {string} faseId @return {Object|null} */
export function faseDe(fases, faseId) {
  return (fases || []).find((f) => f.id === faseId) || null;
}

/** @param {Array<Object>} equipos @param {string} equipoId @return {Object|null} */
export function equipoDe(equipos, equipoId) {
  return (equipos || []).find((e) => e.id === equipoId) || null;
}

/**
 * The equipos a uid belongs to.
 * @param {Array<Object>} equipos
 * @param {string} uid
 * @return {Array<Object>}
 */
export function equiposDe(equipos, uid) {
  if (!uid) return [];
  return (equipos || []).filter((e) => limpiarLista(e.miembros).includes(uid));
}

// ---------------------------------------------------------------------------
// Folio
// ---------------------------------------------------------------------------

/**
 * Display form of a folio. Plain number, no padding (Fran, 2026-10-17: at
 * ~6k OTs a year zero-padding runs out and reads worse).
 * @param {number|null|undefined} folio
 * @return {string}
 */
export function folioTexto(folio) {
  const n = Number(folio);
  return Number.isFinite(n) && n > 0 ? `OT ${n}` : 'OT';
}

// ---------------------------------------------------------------------------
// Asignación (same shape as pendientes: asignados[] + asignadoA in lockstep)
// ---------------------------------------------------------------------------

/** @param {Object} ot @return {Array<string>} */
export function normalizarAsignados(ot) {
  if (!ot) return [];
  if (Array.isArray(ot.asignados)) {
    return [...new Set(ot.asignados.filter((u) => typeof u === 'string' && u))];
  }
  return ot.asignadoA ? [ot.asignadoA] : [];
}

/** @param {Object} ot @param {string} uid @return {boolean} */
export function estaAsignado(ot, uid) {
  return Boolean(uid) && normalizarAsignados(ot).includes(uid);
}

/** @param {Array<string>} v @return {Array<string>} */
const unicos = (v) => [...new Set((v || []).filter((u) => typeof u === 'string' && u))];

// ---------------------------------------------------------------------------
// Actividad stamp (feed line on every write)
// ---------------------------------------------------------------------------

export const ACTIVIDAD_TIPOS = [
  'creada', 'editada', 'fase', 'faseTerminada', 'aprobacion', 'asignada', 'mensaje', 'cerrada', 'reabierta', 'relacion',
];

export const ACTIVIDAD_LABELS = {
  creada: 'creó',
  editada: 'editó',
  fase: 'movió',
  faseTerminada: 'terminó la fase',
  aprobacion: 'resolvió la aprobación',
  asignada: 'asignó',
  mensaje: 'escribió',
  cerrada: 'cerró',
  reabierta: 'reabrió',
  relacion: 'relacionó',
};

/**
 * @param {string} tipo
 * @param {{uid?:string, nombre?:string}|null} quien
 * @param {string|null} texto
 * @param {number|null} ts
 * @return {{ultimaActividad:Object}}
 */
export function buildActividad(tipo, quien = null, texto = null, ts = null) {
  return {
    ultimaActividad: {
      tipo: ACTIVIDAD_TIPOS.includes(tipo) ? tipo : 'editada',
      por: quien?.uid || null,
      nombre: quien?.nombre || null,
      texto: texto ? String(texto).slice(0, 140) : null,
      at: ts || Date.now(),
    },
  };
}

// ---------------------------------------------------------------------------
// Builders
// ---------------------------------------------------------------------------

/**
 * Validation for the create/edit dialog.
 * @param {{tipo:string, titulo:string, faseId?:string, fianzaId?:string|null}} args
 * @param {Array<Object>} fases  - the fases of the tipo's flow
 * @param {Object|null} [config]  - resolved config, to check the tipo exists
 * @return {string|null}
 */
export function validarOt({ tipo, faseId, fianzaId = null }, fases = [], config = null) {
  if (!tipo || typeof tipo !== 'string') return 'Elige el tipo de orden.';
  if (config && !flujoDe(config, tipo)) return 'Ese tipo de orden ya no existe.';
  // An endoso modifies a fianza that already exists; without it there is
  // nothing to endorse. An OT de fianza, by contrast, exists to CREATE one.
  if (requiereFianza(tipo) && !fianzaId) return 'Un endoso necesita la fianza que modifica.';
  if (fases.length && faseId && !faseDe(fases, faseId)) return 'Esa fase ya no existe.';
  return null;
}

/**
 * The OT has no name of its own (Fran, 2026-10-19: "con el número como nombre
 * y el tipo y fiado y luego contrato es suficiente"). `titulo` on the doc is
 * derived from the relation so notifications, search and the bitácora still
 * have a line to show; the dialog recomputes it whenever the relation changes.
 *
 * @param {{fiado?:string, contratoNumero?:string, contratoTitulo?:string, fianzaNumero?:string}} args
 * @return {string}
 */
export function tituloDerivado({ fiado = '', contratoNumero = '', contratoTitulo = '', fianzaNumero = '' } = {}) {
  const partes = [];
  partes.push(String(fiado || '').trim() || 'Sin fiado todavía');
  const num = String(contratoNumero || '').trim();
  const tit = String(contratoTitulo || '').trim();
  if (num) partes.push(`Contrato ${num}`);
  else if (tit) partes.push(tit);
  const fz = String(fianzaNumero || '').trim();
  if (fz) partes.push(`Fianza #${fz}`);
  return partes.join(' · ').slice(0, TITULO_MAX_LEN);
}

/** Whether the tipo must name the fianza it works on. @param {string} tipo */
export function requiereFianza(tipo) {
  return tipo === OT_TIPOS.ENDOSO;
}

/**
 * Whether the dialog offers a fianza picker at creation: not for an OT de
 * fianza (the fianza does not exist yet), yes for everything else.
 * @param {string} tipo
 */
export function ofreceFianza(tipo) {
  return tipo !== OT_TIPOS.FIANZA;
}

/**
 * Build a new OT ready to write. `org` and `folio` are required by the rules
 * and NOT defaulted here.
 *
 * @param {Object} args
 * @return {Object}
 */
export function buildOt({
  org,
  folio,
  tipo = OT_TIPOS.FIANZA,
  titulo = '',
  descripcion = '',
  clienteId = null,
  contratoId = null,
  fianzaId = null,
  afianzadora = null,
  faseId = null,
  equipos = null,
  asignados = null,
  fechaCompromiso = null,
  createdBy = null,
  ts = null,
}) {
  const now = ts || Date.now();
  const lista = unicos(asignados);
  return {
    org,
    folio: Number(folio),
    tipo: typeof tipo === 'string' && tipo ? tipo : OT_TIPOS.FIANZA,
    titulo: String(titulo || '').trim().slice(0, TITULO_MAX_LEN),
    descripcion: String(descripcion || '').trim().slice(0, DESCRIPCION_MAX_LEN) || null,
    clienteId: clienteId || null,
    contratoId: contratoId || null,
    fianzaId: fianzaId || null,
    afianzadora: afianzadora || null,
    faseId: faseId || null,
    faseDesde: now,
    // Where the OT has been and with whom: closed intervals + the open one
    // (hasta null). Stats read this, not the mensajes subcollection.
    historialFases: faseId ? [{ faseId, desde: now, hasta: null, asignados: lista }] : [],
    // Set when whoever works the fase declares it done; cleared by the
    // coordinador's relevo (buildCambioFase).
    faseTerminada: null,
    // Every approval or rejection, in order. Nothing is deleted.
    aprobaciones: [],
    equipos: unicos(equipos),
    asignados: lista,
    asignadoA: lista[0] || null,
    estatus: OT_ESTATUS.ABIERTA,
    cierre: null,
    fechaCompromiso: fechaCompromiso ? Number(fechaCompromiso) : null,
    mensajes: 0,
    createdAt: now,
    createdBy: createdBy || null,
    updatedAt: now,
    ...buildActividad('creada', createdBy, titulo, now),
  };
}

/**
 * Update payload for "fase terminada": the person working the fase declares
 * it done; the OT stays put, flagged for the coordinador's relevo.
 *
 * @param {{uid?:string, nombre?:string}|null} quien
 * @param {string} [nota]
 * @param {number|null} [ts]
 * @return {Object}
 */
export function buildFaseTerminada(quien = null, nota = '', ts = null) {
  const now = ts || Date.now();
  const limpia = String(nota || '').trim().slice(0, MOTIVO_MAX_LEN) || null;
  return {
    faseTerminada: { at: now, por: quien?.uid || null, nombre: quien?.nombre || null, nota: limpia },
    updatedAt: now,
    ...buildActividad('faseTerminada', quien, limpia, now),
  };
}

export const APROBACION_RESULTADOS = { APROBADA: 'aprobada', RECHAZADA: 'rechazada' };

/**
 * @param {{resultado:string, motivo?:string}} args
 * @return {string|null}
 */
export function validarAprobacion({ resultado, motivo }) {
  if (!Object.values(APROBACION_RESULTADOS).includes(resultado)) return 'Elige si se aprueba o se rechaza.';
  if (resultado === APROBACION_RESULTADOS.RECHAZADA && !String(motivo || '').trim()) return 'Di por qué se rechaza.';
  return null;
}

/**
 * Update payload that records an approval or a rejection of the current
 * fase. The caller merges it with buildCambioFase (forward on approval,
 * back on rejection) in the same write.
 *
 * @param {Object} ot
 * @param {{resultado:string, motivo?:string, quien?:Object|null, faseNombre?:string, ts?:number|null}} args
 * @return {Object}
 */
export function buildAprobacion(ot, { resultado, motivo = '', quien = null, faseNombre = '', ts = null }) {
  const now = ts || Date.now();
  const limpio = String(motivo || '').trim().slice(0, MOTIVO_MAX_LEN) || null;
  const entrada = {
    faseId: ot?.faseId || null,
    faseNombre: faseNombre || null,
    resultado,
    motivo: limpio,
    at: now,
    por: quien?.uid || null,
    nombre: quien?.nombre || null,
  };
  return {
    aprobaciones: [...limpiarLista(ot?.aprobaciones), entrada],
    updatedAt: now,
    ...buildActividad('aprobacion', quien, `${resultado === APROBACION_RESULTADOS.APROBADA ? 'Aprobada' : 'Rechazada'}${limpio ? `: ${limpio}` : ''}`, now),
  };
}

/**
 * Where a rejection sends the OT by default: the fase before the current one
 * in the flow, and who worked it last (from the historial).
 * @param {Object} ot
 * @param {Array<Object>} fases
 * @return {{faseId:string|null, asignados:Array<string>}}
 */
export function destinoRechazo(ot, fases) {
  const idx = limpiarLista(fases).findIndex((f) => f.id === ot?.faseId);
  const previa = idx > 0 ? fases[idx - 1] : null;
  if (!previa) return { faseId: null, asignados: [] };
  const h = [...limpiarLista(ot?.historialFases)].reverse().find((x) => x.faseId === previa.id && x.hasta != null);
  return { faseId: previa.id, asignados: unicos(h?.asignados) };
}

/**
 * The aprobador's bandeja: open OTs sitting in an approval fase, oldest
 * arrival first.
 * @param {Array<Object>} ots
 * @param {Object} config
 * @return {Array<Object>}
 */
export function otsPorAprobar(ots, config) {
  return limpiarLista(ots)
    .filter((o) => esperaAprobacion(o, config))
    .sort((a, b) => (a.faseDesde || 0) - (b.faseDesde || 0));
}

/**
 * Every approval and rejection across the given OTs (open and closed), newest
 * first, each carrying its OT for display.
 * @param {Array<Object>} ots
 * @return {Array<{ot:Object, faseId:string|null, faseNombre:string|null, resultado:string, motivo:string|null, at:number, por:string|null, nombre:string|null}>}
 */
export function historialAprobaciones(ots) {
  const out = [];
  limpiarLista(ots).forEach((ot) => {
    limpiarLista(ot?.aprobaciones).forEach((a) => out.push({ ...a, ot }));
  });
  return out.sort((a, b) => (b.at || 0) - (a.at || 0));
}

/** @param {Object} ot @return {boolean} */
export function faseEstaTerminada(ot) {
  return isAbierta(ot) && Boolean(ot?.faseTerminada?.at);
}

/**
 * Update payload that moves an OT to another fase: stamps the current
 * interval's `hasta` (and who closed it, when it had been declared done),
 * opens the next one, clears `faseTerminada`. With `asignados`, the relevo
 * also hands the OT to the people who work the new fase; the previous ones
 * stay on the closed interval.
 *
 * @param {Object} ot
 * @param {string} faseId
 * @param {{uid?:string, nombre?:string}|null} quien
 * @param {string} faseNombre  - for the feed line
 * @param {number|null} ts
 * @param {Array<string>|null} [asignados]  - new asignados, null keeps them
 * @return {Object}
 */
export function buildCambioFase(ot, faseId, quien = null, faseNombre = '', ts = null, asignados = null) {
  const now = ts || Date.now();
  const term = ot?.faseTerminada || null;
  const historial = limpiarLista(ot?.historialFases).map((h) => (
    h.hasta == null ? {
      ...h,
      hasta: now,
      asignados: limpiarLista(h.asignados).length ? h.asignados : normalizarAsignados(ot),
      // Declared done earlier, or ended by whoever moves it now.
      terminadaAt: term?.at || now,
      terminadaPor: term?.at ? (term.por || null) : (quien?.uid || null),
    } : h
  ));
  const nuevos = Array.isArray(asignados) ? unicos(asignados) : normalizarAsignados(ot);
  historial.push({ faseId, desde: now, hasta: null, asignados: nuevos });
  return {
    faseId,
    faseDesde: now,
    faseTerminada: null,
    historialFases: historial,
    ...(Array.isArray(asignados) ? { asignados: nuevos, asignadoA: nuevos[0] || null } : {}),
    updatedAt: now,
    ...buildActividad('fase', quien, faseNombre || faseId, now),
  };
}

/**
 * @param {Array<string>} uids
 * @param {{uid?:string, nombre?:string}|null} quien
 * @param {string|null} texto  - assignee names, for the feed
 * @param {number|null} ts
 * @return {Object}
 */
export function buildAsignacionOt(uids, quien = null, texto = null, ts = null) {
  const lista = unicos(uids);
  const now = ts || Date.now();
  return {
    asignados: lista,
    asignadoA: lista[0] || null,
    updatedAt: now,
    ...buildActividad('asignada', quien, texto, now),
  };
}

/**
 * @param {Array<string>} equipoIds
 * @param {{uid?:string, nombre?:string}|null} quien
 * @param {string|null} texto
 * @param {number|null} ts
 * @return {Object}
 */
export function buildEquiposOt(equipoIds, quien = null, texto = null, ts = null) {
  const now = ts || Date.now();
  return {
    equipos: unicos(equipoIds),
    updatedAt: now,
    ...buildActividad('editada', quien, texto, now),
  };
}

/**
 * Validation for the close dialog. A resultado is always required; the motivo
 * is required when the fianza was NOT delivered, because then the folio alone
 * does not explain why the work stopped.
 * @param {{resultado:string, motivo?:string}} args
 * @return {string|null}
 */
export function validarCierre({ resultado, motivo }) {
  if (!Object.values(OT_RESULTADOS).includes(resultado)) return 'Dinos cómo terminó.';
  if (resultado !== OT_RESULTADOS.ENTREGADA && !String(motivo || '').trim()) {
    return 'Dinos por qué se cierra.';
  }
  return null;
}

/**
 * Update payload that closes an OT. The open fase interval is closed too so
 * time-per-fase stats stop counting.
 *
 * @param {Object} ot
 * @param {{resultado:string, motivo?:string, user?:Object, nombre?:string, ts?:number}} args
 * @return {Object}
 */
export function buildCierre(ot, { resultado, motivo = '', user = null, nombre = null, ts = null }) {
  const now = ts || Date.now();
  const historial = limpiarLista(ot?.historialFases).map((h) => (
    h.hasta == null ? { ...h, hasta: now } : h
  ));
  return {
    estatus: OT_ESTATUS.CERRADA,
    cierre: {
      at: now,
      por: user?.uid || null,
      nombre: nombre || null,
      resultado,
      motivo: String(motivo || '').trim().slice(0, MOTIVO_MAX_LEN) || null,
    },
    historialFases: historial,
    updatedAt: now,
    ...buildActividad('cerrada', { uid: user?.uid, nombre }, OT_RESULTADO_LABELS[resultado] || resultado, now),
  };
}

/**
 * Update payload that reopens a closed OT: back into the fase it was closed
 * in, the cierre kept under `cierres[]` so nothing is lost.
 *
 * @param {Object} ot
 * @param {{motivo?:string, user?:Object, nombre?:string, ts?:number}} args
 * @return {Object}
 */
export function buildReapertura(ot, { motivo = '', user = null, nombre = null, ts = null } = {}) {
  const now = ts || Date.now();
  const previos = limpiarLista(ot?.cierres);
  const historial = limpiarLista(ot?.historialFases);
  if (ot?.faseId) historial.push({ faseId: ot.faseId, desde: now, hasta: null });
  return {
    estatus: OT_ESTATUS.ABIERTA,
    cierre: null,
    cierres: ot?.cierre ? [...previos, {
      ...ot.cierre,
      reabiertaAt: now,
      reabiertaPor: user?.uid || null,
      reabiertaMotivo: String(motivo || '').trim().slice(0, MOTIVO_MAX_LEN) || null,
    }] : previos,
    faseDesde: now,
    historialFases: historial,
    updatedAt: now,
    ...buildActividad('reabierta', { uid: user?.uid, nombre }, motivo, now),
  };
}

/**
 * A message or a system event for the `mensajes` subcollection. Events ride
 * the same stream so the thread doubles as the OT's historial.
 *
 * @param {Object} args
 * @param {'mensaje'|'evento'} args.tipo
 * @param {string} [args.texto]  - the message body (mensaje)
 * @param {string} [args.evento]  - fase | faseTerminada | asignacion | equipos | cierre | reapertura | relacion | creada
 * @param {Object} [args.datos]  - event payload (de, a, nombres...)
 * @param {{uid?:string, nombre?:string}|null} args.autor
 * @param {Array<string>} [args.menciones]  - uids tagged with @"Nombre"
 * @param {number|null} [args.ts]
 * @return {Object}
 */
export function buildMensaje({ tipo = 'mensaje', texto = '', evento = null, datos = null, autor = null, menciones = [], ts = null }) {
  const now = ts || Date.now();
  return {
    tipo: tipo === 'evento' ? 'evento' : 'mensaje',
    texto: tipo === 'evento' ? null : String(texto || '').trim().slice(0, MENSAJE_MAX_LEN),
    evento: tipo === 'evento' ? evento : null,
    datos: tipo === 'evento' ? (datos || {}) : null,
    autor: autor?.uid || null,
    autorNombre: autor?.nombre || null,
    menciones: tipo === 'evento' ? [] : unicos(menciones),
    at: now,
  };
}

// Tags in the hilo are written @"Nombre de la persona" (Fran, 2026-10-19).
export const MENCION_RE = /@"([^"\n]{1,80})"/g;

const normalizarNombre = (v) => String(v || '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/**
 * The org members tagged in a message: every @"Nombre" that matches a
 * member's nombre (accent- and case-insensitive), in order, once each.
 * @param {string} texto
 * @param {Array<{id:string, nombre?:string}>} miembros
 * @return {Array<string>}  uids
 */
export function extraerMenciones(texto, miembros = []) {
  const porNombre = new Map();
  limpiarLista(miembros).forEach((m) => {
    const k = normalizarNombre(m.nombre);
    if (k && !porNombre.has(k)) porNombre.set(k, m.id);
  });
  const out = [];
  String(texto || '').replace(MENCION_RE, (_, nombre) => {
    const uid = porNombre.get(normalizarNombre(nombre));
    if (uid && !out.includes(uid)) out.push(uid);
    return '';
  });
  return out;
}

/**
 * The message split into plain runs and mentions, for rendering.
 * @param {string} texto
 * @return {Array<{tipo:'texto'|'mencion', valor:string}>}
 */
export function partirMenciones(texto) {
  const partes = [];
  const src = String(texto || '');
  let last = 0;
  src.replace(MENCION_RE, (match, nombre, offset) => {
    if (offset > last) partes.push({ tipo: 'texto', valor: src.slice(last, offset) });
    partes.push({ tipo: 'mencion', valor: nombre });
    last = offset + match.length;
    return '';
  });
  if (last < src.length) partes.push({ tipo: 'texto', valor: src.slice(last) });
  return partes;
}

// ---------------------------------------------------------------------------
// Notificaciones (the bell)
// ---------------------------------------------------------------------------

export const NOTIF_TIPOS = {
  ASIGNACION: 'asignacion',
  FASE: 'fase',
  FASE_TERMINADA: 'faseTerminada',
  APROBACION: 'aprobacion',
  MENSAJE: 'mensaje',
  MENCION: 'mencion',
  PENDIENTE: 'pendiente',
  CIERRE: 'cierre',
};

/**
 * One notification row per recipient. The actor is never a recipient, and
 * duplicates collapse.
 *
 * @param {Object} args
 * @param {string} args.org
 * @param {Array<string>} args.para  - uids
 * @param {string} args.tipo  - NOTIF_TIPOS
 * @param {Object} args.ot  - {id, folio, titulo}
 * @param {string} args.texto
 * @param {{uid?:string, nombre?:string}|null} args.por
 * @param {number|null} [args.ts]
 * @return {Array<Object>}
 */
export function buildNotificaciones({ org, para, tipo, ot, texto, por = null, ts = null }) {
  const now = ts || Date.now();
  return unicos(para)
    .filter((uid) => uid !== por?.uid)
    .map((uid) => ({
      org,
      uid,
      tipo: Object.values(NOTIF_TIPOS).includes(tipo) ? tipo : NOTIF_TIPOS.MENSAJE,
      otId: ot?.id || null,
      folio: ot?.folio ?? null,
      titulo: ot?.titulo || '',
      texto: String(texto || '').slice(0, 200),
      por: por?.uid || null,
      porNombre: por?.nombre || null,
      leida: false,
      createdAt: now,
    }));
}

/**
 * Who hears about an OT entering a fase: the members of the fase's equipo
 * plus the OT's asignados.
 * @param {Object} ot
 * @param {Object|null} fase
 * @param {Array<Object>} equipos
 * @return {Array<string>}
 */
export function destinatariosFase(ot, fase, equipos) {
  const equipo = fase?.equipoId ? equipoDe(equipos, fase.equipoId) : null;
  return unicos([...(equipo ? limpiarLista(equipo.miembros) : []), ...normalizarAsignados(ot)]);
}

/**
 * Who hears that a fase was declared done: the coordinadores. The owner is
 * not implied here; esCoordinador's fallback is a read-side convenience.
 * @param {Object} config  - resolved
 * @return {Array<string>}
 */
export function destinatariosFaseTerminada(config) {
  return unicos(limpiarLista(config?.coordinadores));
}

/**
 * Who hears that an OT needs an approval: the aprobadores, or the
 * coordinadores while none are named.
 * @param {Object} config
 * @return {Array<string>}
 */
export function destinatariosAprobacion(config) {
  const a = unicos(limpiarLista(config?.aprobadores));
  return a.length ? a : unicos(limpiarLista(config?.coordinadores));
}

/**
 * Who hears about a message: the asignados plus everyone who has written in
 * the thread.
 * @param {Object} ot
 * @param {Array<Object>} mensajes
 * @return {Array<string>}
 */
export function destinatariosMensaje(ot, mensajes = []) {
  return unicos([
    ...normalizarAsignados(ot),
    ...limpiarLista(mensajes).filter((m) => m.tipo === 'mensaje').map((m) => m.autor),
  ]);
}

// ---------------------------------------------------------------------------
// Reading
// ---------------------------------------------------------------------------

/** @param {Object} ot @return {boolean} */
export function isAbierta(ot) {
  return ot?.estatus !== OT_ESTATUS.CERRADA;
}

/**
 * Bandeja, first block: open OTs whose fase was declared done and wait for
 * the coordinador to advance them, oldest declaration first.
 * @param {Array<Object>} ots
 * @return {Array<Object>}
 */
export function otsPorAvanzar(ots) {
  return limpiarLista(ots)
    .filter(faseEstaTerminada)
    .sort((a, b) => (a.faseTerminada.at || 0) - (b.faseTerminada.at || 0));
}

/**
 * Bandeja, second block: open OTs sitting in a normal fase with nobody on
 * them (typically just advanced out of an approval), oldest arrival first.
 * Approval fases and terminated fases are not here.
 * @param {Array<Object>} ots
 * @param {Object} config  - resolved
 * @return {Array<Object>}
 */
export function otsSinResponsable(ots, config) {
  return limpiarLista(ots)
    .filter((o) => isAbierta(o) && Boolean(o.faseId) && !o.faseTerminada?.at
      && normalizarAsignados(o).length === 0 && !esperaAprobacion(o, config))
    .sort((a, b) => (a.faseDesde || 0) - (b.faseDesde || 0));
}

/**
 * Both blocks together, for counts. Fran (2026-10-19) wants them SHOWN apart:
 * "son dos cosas muy distintas".
 * @param {Array<Object>} ots
 * @param {Object} [config]
 * @return {Array<Object>}
 */
export function otsPorAsignar(ots, config = null) {
  return [...otsPorAvanzar(ots), ...(config ? otsSinResponsable(ots, config) : [])];
}

/**
 * Whether a uid should see this OT in "Mías": assigned to it, or a member of
 * one of its equipos / its current fase's equipo.
 * @param {Object} ot
 * @param {string} uid
 * @param {Object} config  - resolved config (equipos + flujos)
 * @return {boolean}
 */
export function esMia(ot, uid, config) {
  if (!uid) return false;
  if (estaAsignado(ot, uid)) return true;
  const mis = equiposDe(config?.equipos, uid).map((e) => e.id);
  if (!mis.length) return false;
  if (limpiarLista(ot?.equipos).some((e) => mis.includes(e))) return true;
  const fase = faseDe(fasesDe(config, ot?.tipo), ot?.faseId);
  return Boolean(fase?.equipoId && mis.includes(fase.equipoId));
}

const DIA = 86400000;

/** Whole days between two instants (floor). */
export const diasEntre = (desdeMs, hastaMs) => Math.max(0, Math.floor((hastaMs - desdeMs) / DIA));

/**
 * Days the OT has spent in its current fase.
 * @param {Object} ot
 * @param {number} [nowMs]
 * @return {number}
 */
export function diasEnFase(ot, nowMs = Date.now()) {
  return diasEntre(Number(ot?.faseDesde || ot?.createdAt || nowMs), nowMs);
}

/**
 * Where the OT stands against its fase's días objetivo.
 * @param {Object} ot
 * @param {Object|null} fase
 * @param {number} [nowMs]
 * @return {'sinObjetivo'|'enTiempo'|'porVencer'|'vencida'}
 */
export function estadoFase(ot, fase, nowMs = Date.now()) {
  if (!fase?.diasObjetivo) return 'sinObjetivo';
  const d = diasEnFase(ot, nowMs);
  if (d > fase.diasObjetivo) return 'vencida';
  if (d >= fase.diasObjetivo) return 'porVencer';
  return 'enTiempo';
}

/**
 * Days since the OT was opened — until its cierre when closed.
 * @param {Object} ot
 * @param {number} [nowMs]
 * @return {number}
 */
export function diasAbierta(ot, nowMs = Date.now()) {
  const fin = ot?.cierre?.at ? Number(ot.cierre.at) : nowMs;
  return diasEntre(Number(ot?.createdAt || fin), fin);
}

/**
 * Open OTs grouped by fase, in config order; OTs whose fase is gone land in
 * `sinFase`.
 * @param {Array<Object>} ots
 * @param {Array<Object>} fases
 * @return {{columnas:Array<{fase:Object, rows:Array<Object>}>, sinFase:Array<Object>}}
 */
export function agruparPorFase(ots, fases) {
  const porFase = {};
  (fases || []).forEach((f) => { porFase[f.id] = []; });
  const sinFase = [];
  (ots || []).forEach((ot) => {
    if (ot.faseId && porFase[ot.faseId]) porFase[ot.faseId].push(ot);
    else sinFase.push(ot);
  });
  return {
    columnas: (fases || []).map((fase) => ({ fase, rows: porFase[fase.id] })),
    sinFase,
  };
}

/**
 * Open OTs per person, for the Carga view. People with zero are included when
 * passed in `miembros` so the owner sees who is free.
 * @param {Array<Object>} ots  - open OTs
 * @param {Array<{id:string}>} miembros
 * @return {Array<{uid:string, total:number, porTipo:Object<string,number>, masVieja:number|null}>}
 */
export function cargaPorPersona(ots, miembros = [], nowMs = Date.now()) {
  const acc = {};
  const touch = (uid) => {
    if (!acc[uid]) acc[uid] = { uid, total: 0, porTipo: {}, masVieja: null };
    return acc[uid];
  };
  (miembros || []).forEach((m) => touch(m.id));
  (ots || []).filter(isAbierta).forEach((ot) => {
    normalizarAsignados(ot).forEach((uid) => {
      const r = touch(uid);
      r.total += 1;
      const t = ot.tipo || OT_TIPOS.FIANZA;
      r.porTipo[t] = (r.porTipo[t] || 0) + 1;
      const d = diasAbierta(ot, nowMs);
      r.masVieja = r.masVieja === null ? d : Math.max(r.masVieja, d);
    });
  });
  return Object.values(acc).sort((a, b) => b.total - a.total);
}

/**
 * Open OTs per equipo: the ones explicitly assigned to the equipo plus the
 * ones sitting in a fase the equipo owns.
 * @param {Array<Object>} ots  - open OTs
 * @param {Object} config  - resolved config (equipos + flujos)
 * @return {Array<{equipo:Object, asignadas:number, enSuFase:number, total:number}>}
 */
export function cargaPorEquipo(ots, config) {
  const resuelta = resolverConfigOt(config);
  const { equipos } = resuelta;
  const fases = todasLasFases(resuelta);
  return equipos.map((equipo) => {
    const fasesSuyas = fases.filter((f) => f.equipoId === equipo.id).map((f) => f.id);
    let asignadas = 0;
    let enSuFase = 0;
    let total = 0;
    (ots || []).filter(isAbierta).forEach((ot) => {
      const a = limpiarLista(ot.equipos).includes(equipo.id);
      const f = fasesSuyas.includes(ot.faseId);
      if (a) asignadas += 1;
      if (f) enSuFase += 1;
      if (a || f) total += 1;
    });
    return { equipo, asignadas, enSuFase, total };
  });
}

/**
 * Processing statistics over a set of OTs.
 *
 * @param {Array<Object>} ots  - every OT, open and closed
 * @param {Object} config  - resolved config (equipos + flujos)
 * @param {number} [nowMs]
 * @return {Object}
 */
export function estadisticasOt(ots, config, nowMs = Date.now()) {
  const todas = ots || [];
  const abiertas = todas.filter(isAbierta);
  const cerradas = todas.filter((o) => !isAbierta(o));
  const hace30 = nowMs - 30 * DIA;
  const hace90 = nowMs - 90 * DIA;

  const cerradas30 = cerradas.filter((o) => Number(o.cierre?.at || 0) >= hace30);
  const abiertas30 = todas.filter((o) => Number(o.createdAt || 0) >= hace30);
  const cerradas90 = cerradas.filter((o) => Number(o.cierre?.at || 0) >= hace90);

  const promedio = (xs) => (xs.length ? Math.round((xs.reduce((a, b) => a + b, 0) / xs.length) * 10) / 10 : null);

  const diasCierre = promedio(cerradas90.map((o) => diasAbierta(o, nowMs)));
  const entregadas90 = cerradas90.filter((o) => o.cierre?.resultado === OT_RESULTADOS.ENTREGADA).length;

  // Time per fase, per flow: every closed interval in historialFases plus the
  // open one measured to now, averaged per fase, next to the días objetivo.
  // OTs whose tipo is no longer configured are counted in `otros`.
  const flujos = (config?.flujos || []).map((flujo) => {
    const fases = flujo.fases || [];
    const porFase = {};
    fases.forEach((f) => { porFase[f.id] = { fase: f, abiertas: 0, vencidas: 0, muestras: [] }; });
    const delTipo = todas.filter((o) => (o.tipo || OT_TIPOS.FIANZA) === flujo.id);
    delTipo.forEach((o) => {
      limpiarLista(o.historialFases).forEach((h) => {
        const b = porFase[h.faseId];
        if (!b) return;
        const fin = h.hasta == null ? nowMs : Number(h.hasta);
        b.muestras.push((fin - Number(h.desde)) / DIA);
      });
      if (isAbierta(o) && porFase[o.faseId]) {
        porFase[o.faseId].abiertas += 1;
        if (estadoFase(o, porFase[o.faseId].fase, nowMs) === 'vencida') porFase[o.faseId].vencidas += 1;
      }
    });
    const cerradasTipo90 = delTipo.filter((o) => !isAbierta(o) && Number(o.cierre?.at || 0) >= hace90);
    return {
      flujo,
      fases: Object.values(porFase).map((b) => ({
        fase: b.fase,
        abiertas: b.abiertas,
        vencidas: b.vencidas,
        promedioDias: promedio(b.muestras),
        muestras: b.muestras.length,
      })),
      objetivoTotal: diasObjetivoTotal(fases),
      diasPromedioCierre: promedio(cerradasTipo90.map((o) => diasAbierta(o, nowMs))),
      abiertas: delTipo.filter(isAbierta).length,
    };
  });

  const porTipo = {};
  flujos.forEach((f) => { porTipo[f.flujo.id] = f.abiertas; });
  const conocidos = new Set(flujos.map((f) => f.flujo.id));
  const otros = abiertas.filter((o) => !conocidos.has(o.tipo || OT_TIPOS.FIANZA)).length;
  if (otros) porTipo.otros = otros;

  // Oldest open OTs, the ones a supervisor asks about first.
  const masViejas = [...abiertas]
    .sort((a, b) => Number(a.createdAt || 0) - Number(b.createdAt || 0))
    .slice(0, 5);

  return {
    abiertas: abiertas.length,
    cerradas: cerradas.length,
    abiertas30: abiertas30.length,
    cerradas30: cerradas30.length,
    entregadas90,
    cerradas90: cerradas90.length,
    diasPromedioCierre: diasCierre,
    porTipo,
    flujos,
    masViejas,
  };
}

/**
 * Sort for the lists: open first, then most recently active.
 * @param {Array<Object>} ots
 * @return {Array<Object>}
 */
export function ordenarOts(ots) {
  return [...(ots || [])].sort((a, b) => {
    const ea = isAbierta(a) ? 0 : 1;
    const eb = isAbierta(b) ? 0 : 1;
    if (ea !== eb) return ea - eb;
    return Number(b.updatedAt || 0) - Number(a.updatedAt || 0);
  });
}
